import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createWebsite } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

export default function AddWebsiteDialog({ disabled }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: () => createWebsite({ name, domain }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["websites"] });
      toast.success("Website added");
      setOpen(false);
      setName("");
      setDomain("");
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Failed to add website");
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="open-add-website"
          disabled={disabled}
          className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white px-4 h-10 flex items-center gap-2"
        >
          <Plus size={16} weight="bold" />
          Add website
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border border-[var(--rt-border)] bg-white">
        <DialogHeader>
          <div className="label-eyebrow mb-2">/ new tracking target</div>
          <DialogTitle className="font-display text-2xl">Add a website</DialogTitle>
          <DialogDescription className="text-sm text-[var(--rt-text-muted)]">
            Enter the domain you want to rank-track. We&apos;ll normalize https:// and www. for you.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="label-eyebrow">Display name</Label>
            <Input
              data-testid="website-name-input"
              className="rounded-none border-[var(--rt-border)] focus:border-[var(--rt-primary)] mt-1.5"
              value={name}
              placeholder="My Coffee Shop"
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <Label className="label-eyebrow">Domain</Label>
            <Input
              data-testid="website-domain-input"
              className="rounded-none border-[var(--rt-border)] focus:border-[var(--rt-primary)] mt-1.5 font-mono-data"
              value={domain}
              placeholder="example.com"
              onChange={(e) => setDomain(e.target.value)}
            />
            <p className="text-xs text-[var(--rt-text-muted)] mt-1.5">
              Strip https:// and www. — we&apos;ll normalize it.
            </p>
          </div>
        </div>
        <DialogFooter className="mt-6">
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            className="rounded-none"
            data-testid="cancel-add-website"
          >
            Cancel
          </Button>
          <Button
            data-testid="submit-add-website"
            disabled={!domain || mut.isPending}
            onClick={() => mut.mutate()}
            className="rounded-none bg-[var(--rt-primary)] hover:bg-[var(--rt-primary-hover)] text-white"
          >
            {mut.isPending ? "Adding…" : "Add website"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
